import {Pages} from 'shared/config/model/pages';
import {AppUrls} from 'shared/config/model/urls';

import React, {useCallback, useState} from 'react';
import {useSelector} from 'react-redux';
import {userSelector, userAPI, Permission} from 'entities/user';
import {i18n} from '@lingui/core';

import ListPage from 'shared/ui/ListPage/ListPage';
import PaymentUserListStatsWidget from 'features/admin/ui/User/PaymentsWidget/PaymentsWidget';
import ListToolbar from 'shared/ui/ListToolbar/ListToolbar';
import TableToolbar from 'shared/ui/TableToolbar/TableToolbar';
import ListFiltersModal from 'shared/ui/Modal/ListFiltersModal/ListFiltersModal';
import AddPaymentButton from 'features/admin/ui/AddPaymentButton/AddPaymentButton';
import {IBatchResult, useBatchOperation} from 'entities/monitor';
import {getPollingInterval} from 'shared/api/pollingHelper';
import useListParams from 'shared/lib/hooks/useListParams';
import useResetPageOnEmpty from 'shared/lib/hooks/useResetPageOnEmpty';
import {buildUsersFiltersArray} from 'features/admin/lib/userFiltersParams';

import {t} from '@lingui/core/macro';

import UserRow from './UserRow';
import UserFiltersForm from './UserFilters/UserFiltersForm/UserFiltersForm';
import UsersExportModal from './UsersExport/UsersExportModal';
import {EXPORT_ITEMS, IExportItem} from './UsersExport/exportItems';

const Users: React.FC = () => {
    const [filtersModal, setIsFilterOpen] = useState(false);
    const [exportModal, setExportModal] = useState(false);
    const [exportItem, setExportItem] = useState<IExportItem>();
    const [result, setResult] = useState<IBatchResult | undefined>();

    const {trigger, progress} = useBatchOperation();

    const user = useSelector(userSelector);
    const {args, sort, setSort, search, onSearch} = useListParams({
        getFilters: buildUsersFiltersArray,
        defaultSort: {field: 'id', direction: 'desc'},
    });

    const handleOpenFilter = useCallback(() => setIsFilterOpen(true), []);
    const handleCloseFilter = useCallback(() => setIsFilterOpen(false), []);


    const {data: pagination, isLoading, refetch, isFetching} = userAPI.useGetUsersQuery(args, {
        pollingInterval: getPollingInterval(),
    });
    const isRedirecting = useResetPageOnEmpty(pagination);

    const handleExport = async(item: IExportItem) => {
        setResult(undefined);
        setExportItem(item);
        setExportModal(true);
        await trigger(
            item.url,
            'POST',
            {},
            (result: IBatchResult) => {
                setResult(result);
            },
        );
    };

    const exportItems = EXPORT_ITEMS.map(item => ({
        key: item.kind,
        label: item.label,
        menuLabel: item.menuLabel,
        onClick: () => handleExport(item),
    }));


    return (
        <ListPage
            documentTitle={`${t`Пользователи сервиса`} | ${t`Overseer`}`}
            toolbar={
                <ListToolbar
                    title={i18n._(Pages.USERS)}
                    count={pagination?.totalItems}
                    backUrl={AppUrls.SETTINGS}
                    createButton={<AddPaymentButton/>}
                    hideCreateButtonOnMobile
                    actions={
                        <TableToolbar
                            onSearch={onSearch}
                            searchValue={search}
                            onFilter={handleOpenFilter}
                            exportItems={exportItems}
                            onRefresh={refetch}
                            isRefreshing={isFetching}
                        />
                    }
                />
            }
            actions={user?.roles.includes(Permission.ADMIN) ? <PaymentUserListStatsWidget/> : undefined}
            columns={[
                {label: 'ID', sort: 'id'},
                {label: 'Дата регистрации', sort: 'created_at'},
                {label: 'Последняя активность', sort: 'last_activity_at'},
                {label: 'Имя', sort: 'first_name'},
                {label: 'Фамилия', sort: 'last_name'},
                {label: 'Email', sort: 'email'},
                {label: 'Пояс', sort: 'timezone'},
                {label: 'Тариф', sort: 'tariff.name'},
                {label: 'Активен до', sort: 'active_to'},
                {label: '∞', sort: 'is_recurring'},
                {label: '👁/⚠️'},
                {label: 'Согласие на рассылку', sort: 'agree_to_newsletter'},
                {label: ''},
            ]}
            items={pagination?.items}
            rowFactory={item => <UserRow
                key={item.id}
                item={item}
                tzString={user?.timezone}
            />}
            isLoading={isLoading || isRedirecting}
            pagination={pagination}
            sort={sort}
            setSort={setSort}
        >
            <ListFiltersModal
                open={filtersModal}
                handleClose={handleCloseFilter}
                Form={UserFiltersForm}
            />
            <UsersExportModal
                open={exportModal}
                handleClose={() => setExportModal(false)}
                result={result}
                entityName={exportItem?.entityName}
                progress={progress}
            />
        </ListPage>
    );
};

export default Users;
